import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  TextInput,
  PasswordInput,
  Title,
  Stack,
  Flex,
  Anchor,
  Divider,
  Container,
  Text,
  Paper,
  Center,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { myFetch } from '@commons/fetch';
import routes from '@commons/routes';
import { Link as ReactRouterLink, useNavigate } from 'react-router';
import { useUser } from '@/contexts/UserContext';

export const UserRegister = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [captchaText, setCaptchaText] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const { user, logoutWithCallback } = useUser();
  const [isValidating, setIsValidating] = useState(true);

  const intl = useIntl();
  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      if (!user) {
        setIsValidating(false);
        return;
      }

      try {
        setIsValidating(true);
        const response = await myFetch(`/users/token/verify`, {
          method: 'GET',
        });

        const { user } = response.data;

        navigate(routes.dashboard(user.username), { replace: true });
      } catch (error: any) {
        notifications.show({
          title: intl.formatMessage({ id: 'error' }),
          message: error.message,
          color: 'red',
        });

        logoutWithCallback(() => {});

        setIsValidating(false);
      }
    };

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadCaptcha = async () => {
      try {
        const response = await myFetch(`/captcha`, {
          method: 'GET',
        });

        setCaptchaSvg(response.data.svg);
        setCaptchaToken(response.data.token);
      } catch (err: any) {
        notifications.show({
          title: intl.formatMessage({ id: 'error' }),
          message: err?.message || intl.formatMessage({ id: 'error' }),
          color: 'red',
        });
      }
    };

    loadCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaKey]);

  const refreshCaptcha = () => {
    setCaptchaText('');
    setCaptchaToken('');
    setCaptchaKey((prev) => prev + 1);
  };

  const handleRegister = async () => {
    if (!username || !email || !password || !confirmPassword) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'pleaseEnterAllFields',
          defaultMessage: 'Please enter all fields',
        }),
        color: 'red',
      });
      return;
    }

    if (password !== confirmPassword) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'passwordsDoNotMatch',
          defaultMessage: 'Passwords do not match',
        }),
        color: 'red',
      });
      return;
    }

    if (password.length < 6) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'passwordTooShort',
          defaultMessage: 'Password must be at least 6 characters',
        }),
        color: 'red',
      });
      return;
    }

    if (username.length > 50) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'usernameTooLong',
          defaultMessage: 'Username must be at most 50 characters',
        }),
        color: 'red',
      });
      return;
    }

    if (!captchaText) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'pleaseCompleteCaptcha',
          defaultMessage: 'Please complete the CAPTCHA verification',
        }),
        color: 'red',
      });
      return;
    }

    setIsLoading(true);

    if (!captchaToken) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'errorLoadingCaptcha',
          defaultMessage: 'CAPTCHA not loaded. Please refresh the page.',
        }),
        color: 'red',
      });
      setIsLoading(false);
      return;
    }

    try {
      await myFetch(`/users`, {
        method: 'POST',
        body: JSON.stringify({
          username,
          email,
          password,
          captchaText,
          captchaToken,
        }),
      });

      notifications.show({
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({
          id: 'registrationSuccessful',
          defaultMessage: 'Registration successful! Please log in.',
        }),
        color: 'green',
      });

      navigate(routes.routes.userLogin);
    } catch (err: any) {
      setIsLoading(false);

      refreshCaptcha();

      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
        color: 'red',
      });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleRegister();
    }
  };

  // Show nothing while validating existing session
  if (isValidating) {
    return null;
  }

  return (
    <Center mih="100svh" p="md">
      <Container size="xs" maw="100svw">
        <Stack gap="md">
          <Title order={2}>
            {intl.formatMessage({
              id: 'createAccount',
              defaultMessage: 'Create Account',
            })}
          </Title>

          <TextInput
            label={intl.formatMessage({
              id: 'username',
              defaultMessage: 'Username',
            })}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
            description={intl.formatMessage({
              id: 'usernameDescription',
            })}
            autoFocus
          />

          <TextInput
            type="email"
            label={intl.formatMessage({
              id: 'email',
            })}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
            description={intl.formatMessage({
              id: 'emailDescription',
            })}
          />

          <PasswordInput
            label={intl.formatMessage({
              id: 'password',
            })}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
            description={intl.formatMessage({
              id: 'passwordDescription',
            })}
          />

          <PasswordInput
            label={intl.formatMessage({
              id: 'confirmPassword',
            })}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
          />

          <Stack gap="xs" mt="md">
            <Text fz="sm" fw={500}>
              {intl.formatMessage({
                id: 'captchaVerification',
              })}
            </Text>

            <Flex gap="xs" align="center">
              <Paper
                p="xs"
                withBorder
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 60,
                }}
                dangerouslySetInnerHTML={{ __html: captchaSvg }}
              />
              <Button
                id="refresh-captcha-button"
                variant="default"
                onClick={refreshCaptcha}
                disabled={isLoading}
              >
                {intl.formatMessage({
                  id: 'refreshCaptcha',
                  defaultMessage: 'Refresh',
                })}
              </Button>
            </Flex>

            <TextInput
              label={intl.formatMessage({
                id: 'enterCaptcha',
              })}
              value={captchaText}
              onChange={(e) => setCaptchaText(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isLoading}
              description={intl.formatMessage({
                id: 'captchaDescription',
              })}
            />
          </Stack>

          <Flex justify="space-between" align="center" mt="xs">
            <Anchor
              component={ReactRouterLink}
              to={routes.routes.userLogin}
              size="sm"
            >
              {intl.formatMessage({
                id: 'alreadyHaveAccount',
              })}
            </Anchor>

            <Button
              id="register-button"
              variant="filled"
              onClick={handleRegister}
              loading={isLoading}
            >
              {intl.formatMessage({
                id: 'register',
              })}
            </Button>
          </Flex>

          <Divider my="md" />

          <Anchor component={ReactRouterLink} to={routes.root} size="sm">
            {intl.formatMessage({
              id: 'backToHome',
            })}
          </Anchor>
        </Stack>
      </Container>
    </Center>
  );
};
